import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MiniPriceChart from '../components/MiniPriceChart';
import './SalePage.css';

function SalePage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('day'); // 'day', 'week', 'month'
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('bestDiscount'); // 'bestDiscount', 'priceLow', 'priceHigh', 'name', 'priceChangeBest', 'avgPrice'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' oder 'desc'

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError('');
      
      // OPTIMIERT: 2 Calls statt 2N+1 — alle Daten parallel laden
      const [productsRes, diffRes] = await Promise.all([
        fetch('/api/products/with-prices/all?onlineOnly=true'),
        fetch('/api/prices/bulk-price-differences')
      ]);
      
      if (!productsRes.ok) {
        throw new Error('Produkte konnten nicht geladen werden');
      }

      const data = await productsRes.json();
      const diffData = await diffRes.json();
      
      if (!Array.isArray(data)) {
        throw new Error('Ungültige Datenformat');
      }

      // Merge: Preisdaten direkt aus der Bulk-Response zuordnen
      const productsWithDifference = data
        .filter(p => p.bestPrice !== null && p.avgPrice !== null)
        .map(p => {
          const differenceVsAvg = ((p.bestPrice - p.avgPrice) / p.avgPrice) * 100;
          const diff = diffData[p._id] || {};
          
          return {
            ...p,
            differenceVsAvg,
            yesterdayBestPrice: diff.yesterdayBestPrice || null,
            priceChangeVsYesterday: diff.priceChangeVsYesterday || null,
            currentBestPrice: diff.currentBestPrice || p.bestPrice,
            priceChanges: diff.priceChanges || { day: null, week: null, month: null },
            avgPriceChangeVsYesterday: diff.avgPriceChangeVsYesterday || null
          };
        });

      // Sortiere nach größtem Rabatt (negativster Wert = größter Rabatt)
      productsWithDifference.sort((a, b) => a.differenceVsAvg - b.differenceVsAvg);

      setProducts(productsWithDifference);
    } catch (err) {
      console.error('Fehler beim Laden der Produkte:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle Sortierung durch Klick auf Spaltenheader
  const handleSort = (column) => {
    if (sortBy === column) {
      // Wenn bereits sortiert nach dieser Spalte, zwischen auf- und absteigend wechseln
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Neue Spalte wählen und aufsteigend sortieren
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (column) => {
    if (sortBy !== column) return ' ⇅';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Filtere und sortiere Produkte
  const getSortedAndFilteredProducts = () => {
    let filtered = [...products];

    // Filter nach Suchquery
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(q) ||
        p.manufacturerName?.toLowerCase().includes(q)
      );
    }

    // Sortieren
    switch (sortBy) {
      case 'bestDiscount':
        // Sortiere nach größtem Rabatt (negativster Wert = größter Rabatt)
        filtered.sort((a, b) => {
          const diff = a.differenceVsAvg - b.differenceVsAvg;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      case 'priceLow':
        // Günster/Teuerster Preis
        filtered.sort((a, b) => {
          const diff = a.bestPrice - b.bestPrice;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      case 'priceHigh':
        // Teuerster/Günstigster Preis
        filtered.sort((a, b) => {
          const diff = b.bestPrice - a.bestPrice;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      case 'name':
        // Alphabetisch nach Name
        filtered.sort((a, b) => {
          const cmp = a.name.localeCompare(b.name);
          return sortOrder === 'asc' ? cmp : -cmp;
        });
        break;
      case 'priceChangeBest':
        // Beste/Schlechteste Preisänderungen
        filtered.sort((a, b) => {
          const aChange = a.priceChangeVsYesterday;
          const bChange = b.priceChangeVsYesterday;
          if (aChange === null || aChange === undefined) return 1;
          if (bChange === null || bChange === undefined) return -1;
          const diff = aChange - bChange;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      case 'avgPrice':
        // Nach Durchschnittspreis
        filtered.sort((a, b) => {
          const diff = a.avgPrice - b.avgPrice;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      case 'avgPriceChange':
        // Nach Durchschnittspreis-Änderung zum Vortag
        filtered.sort((a, b) => {
          const aChange = a.avgPriceChangeVsYesterday;
          const bChange = b.avgPriceChangeVsYesterday;
          if (aChange === null || aChange === undefined) return 1;
          if (bChange === null || bChange === undefined) return -1;
          const diff = aChange - bChange;
          return sortOrder === 'asc' ? diff : -diff;
        });
        break;
      default:
        break;
    }

    return filtered;
  };

  const sortedAndFilteredProducts = getSortedAndFilteredProducts();
  const getPriceColorClass = (value) => {
    if (value === null || value === undefined) return 'neutral';
    if (value < 0) return 'positive'; // Preis gefallen = gut
    if (value > 0) return 'negative'; // Preis gestiegen = schlecht
    return 'neutral';
  };

  // Für Rabatt-Unterschied zum Durchschnitt (negativ = Rabatt = grün)
  const getRabattColorClass = (value) => {
    if (value === null || value === undefined) return 'neutral';
    if (value < 0) return 'positive'; // Günstiger als Durchschnitt = gut
    if (value > 0) return 'negative'; // Teurer als Durchschnitt = schlecht
    return 'neutral';
  };

  const formatPriceChange = (value) => {
    if (value === null || value === undefined) return '–';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '–';
    return `€${price.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="sale-page">
        <div className="loading">⏳ Produkte werden geladen...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sale-page">
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="sale-page">
      <div className="sale-header">
        <h1>💰 SALE & DEALS</h1>
        <p>Produkte nach größtem Rabatt gegenüber dem Durchschnittspreis sortiert</p>
      </div>

      <div className="filter-section">
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Produkt oder Hersteller durchsuchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="time-range-buttons">
          <label>Zeitraum für Chart:</label>
          <div className="buttons-group">
            <button
              className={`time-btn ${timeRange === 'day' ? 'active' : ''}`}
              onClick={() => setTimeRange('day')}
            >
              7 Tage
            </button>
            <button
              className={`time-btn ${timeRange === 'week' ? 'active' : ''}`}
              onClick={() => setTimeRange('week')}
            >
              30 Tage
            </button>
            <button
              className={`time-btn ${timeRange === 'month' ? 'active' : ''}`}
              onClick={() => setTimeRange('month')}
            >
              90 Tage
            </button>
          </div>
        </div>
      </div>

      {products.length === 0 ? (
        <div className="no-products">Keine Produkte mit Preisangeboten gefunden</div>
      ) : sortedAndFilteredProducts.length === 0 ? (
        <div className="no-products">Keine Produkte gefunden, die den Suchkriterien entsprechen</div>
      ) : (
        <div className="products-table">
          <div className="table-header">
            <div className="col col-rank">#</div>
            <div 
              className="col col-product sortable-header"
              onClick={() => handleSort('name')}
              title="Nach Produktname (A-Z) sortieren"
            >
              Produkt{renderSortIcon('name')}
            </div>
            <div 
              className="col col-difference sortable-header"
              onClick={() => handleSort('bestDiscount')}
              title="Nach Rabatt (%) sortieren - größter zu kleinster Rabatt"
            >
              Sale{renderSortIcon('bestDiscount')}
            </div>
            <div 
              className="col col-price sortable-header"
              onClick={() => handleSort('priceChangeBest')}
              title="Nach Preisveränderung zum Vortag (%) sortieren"
            >
              Günstigster Preis{renderSortIcon('priceChangeBest')}
            </div>
            <div 
              className="col col-avg-price sortable-header"
              onClick={() => handleSort('avgPriceChange')}
              title="Nach Durchschnittspreis-Veränderung zum Vortag (%) sortieren"
            >
              Ø Preis{renderSortIcon('avgPriceChange')}
            </div>
            <div className="col col-chart">Preisverlauf</div>
          </div>

          {sortedAndFilteredProducts.map((product, index) => {
            const priceChangeVsYesterday = product.priceChangeVsYesterday;
            const avgPriceChangeVsYesterday = product.avgPriceChangeVsYesterday;
            const differenceVsAvg = product.differenceVsAvg;

            return (
              <div key={product._id} className="table-row">
                <div className="col col-rank">
                  <span className={`rank-badge ${index < 3 ? 'top-3' : ''}`}>
                    {index < 3 ? ['🥇', '🥈', '🥉'][index] : index + 1}
                  </span>
                </div>
                <div className="col col-product">
                  <div className="product-cell">
                    {product.imageUrl && (
                      <img src={product.imageUrl} alt={product.name} className="product-thumbnail" />
                    )}
                    <div className="product-info">
                      <Link to={`/product/${product._id}/suche`} className="product-name-link">
                        <div className="product-name">{product.name}</div>
                      </Link>
                      <div className="product-meta">
                        {product.manufacturerName && (
                          <span className="manufacturer">{product.manufacturerName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col col-difference">
                  <span className={`difference-badge ${getRabattColorClass(differenceVsAvg)}`}>
                    {differenceVsAvg < 0 ? '' : '+'}{differenceVsAvg?.toFixed(1)}%
                  </span>
                </div>
                <div className="col col-price">
                  <div className="price-change-cell">
                    <div className="price-change-value">{formatPrice(product.bestPrice)}</div>
                    {priceChangeVsYesterday !== null ? (
                      <div className={`price-change-percent ${getPriceColorClass(priceChangeVsYesterday)}`}>
                        {formatPriceChange(priceChangeVsYesterday)}
                      </div>
                    ) : (
                      <div className="price-change-percent neutral">–</div>
                    )}
                  </div>
                </div>
                <div className="col col-avg-price">
                  <div className="avg-price-cell">
                    <div className="avg-price-value">{formatPrice(product.avgPrice)}</div>
                    {avgPriceChangeVsYesterday !== null ? (
                      <div className={`avg-price-change ${getPriceColorClass(avgPriceChangeVsYesterday)}`}>
                        {formatPriceChange(avgPriceChangeVsYesterday)}
                      </div>
                    ) : (
                      <div className="avg-price-change neutral">–</div>
                    )}
                  </div>
                </div>
                <div className="col col-chart">
                  <MiniPriceChart 
                    productId={product._id} 
                    days={timeRange === 'day' ? 7 : timeRange === 'week' ? 30 : 90}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      <div className="legend-section">
        <h3>Legende</h3>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-badge positive">-10%</span>
            <span>Günstiger als Durchschnitt / Preis gefallen</span>
          </div>
          <div className="legend-item">
            <span className="legend-badge negative">+10%</span>
            <span>Teurer als Durchschnitt / Preis gestiegen</span>
          </div>
          <div className="legend-item">
            <span className="legend-chart green"></span>
            <span>Preis fallend (gut)</span>
          </div>
          <div className="legend-item">
            <span className="legend-chart red"></span>
            <span>Preis steigend (teurer)</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalePage;
